/*
The notices of the XOL-596 save flow (UX spec items 1-7 and 9): the one popup per
pause-flush, the "not saved" notices and the 428 prompt. Since C-12 (2026-10-13)
the popup is a section / the Saved text of the ONE save-status card
(lib/savePopup + components/requests/SaveStatusPopup); nothing here raises an
Element $notify or $message any more.

`installSyncNotices(root)` registers, once at app level, the burst reporter
(lib/requestRowSync.setBurstReporter) and the reload prompter. A burst is one
pause-flush (or flush-point flush): when all its saves have settled the reporter
raises ONE popup. No failures: "Saved: …". Any failure: ONE section that replaces
the success and ends with "Anything not listed here was saved." when something
else in the burst did save.

`root` is a Vue instance (the app root) whose prototype carries Element's $confirm
and the app's global mixins (formatDate, formatDateTime, towerCoName, userName,
isTowerCo). The dialogs are built with `root.$createElement`, so they outlive the
drawer that raised them.

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import * as sync from '@/lib/requestRowSync'
import * as savePopup from '@/lib/savePopup'
import StaleSaveNotice from '@/components/requests/StaleSaveNotice'
import SaveStatusPopup from '@/components/requests/SaveStatusPopup'
import Vue from 'vue'
import { copyText, fieldLabel } from '@/lib/requestSyncCopy'
import { plainText, valueKind, describeScalar, listDifference } from '@/lib/syncText'
import { canUnlock } from '@/lib/approvalLock'
import { formatNoticeDate, formatNoticeDateTime } from '@/lib/noticeDates'

// The one card instance (installSyncNotices) and the element it is mounted on.
var popupVm = null

function fmt (root) {
  return {
    date: function (v) { return formatNoticeDate(root, v) },
    datetime: function (v) { return formatNoticeDateTime(root, v) }
  }
}

function towerCo (root) {
  return root.towerCoName ? root.towerCoName() : 'the Tower Co'
}

function labelOf (root, o) {
  var m = o.meta || {}
  return fieldLabel(o.kind, o.path, { partyNo: m.partyNo, stepNo: m.stepNo, form: m.form, towerCo: towerCo(root), telco: m.telcoName || 'the telco' })
}

function rowLabelOf (root, o) {
  var m = o.meta || {}
  // An EditPane form names its own row (UX C-4): "{RTx} #{id} · Action details" /
  // "{RTx} #{id} · Step {n}".
  if (m.form === 'actionDetails') {
    return copyText('requestSync.rowLabel.actionDetails', { rtx: m.rtx, id: m.id })
  }
  if (m.form === 'progressStep') {
    return copyText('requestSync.rowLabel.progressStep', { rtx: m.rtx, id: m.actionId, n: m.stepNo })
  }
  if (o.kind === 'step') {
    return copyText('requestSync.rowLabel.step', {
      rtx: m.rtx,
      id: m.actionId,
      n: m.stepNo,
      title: m.stepName,
      org: (Number(m.partyNo) === 2 && root.isTowerCo && root.isTowerCo()) ? m.orgShort : null
    })
  }
  return copyText('requestSync.rowLabel.action', { rtx: m.rtx, id: m.id })
}

/**
 * One C-1 field block for a failed outcome: the label, what the user typed (the
 * LATEST value, even text typed while the save was in flight), what was saved
 * instead, and who changed it.
 */
export function fieldBlock (root, o) {
  var f = fmt(root)
  var kind = valueKind(o.path, o.value)
  var block = { label: labelOf(root, o), kind: kind }

  if (block.label === copyText('requestSync.field.other')) {
    // An unmapped path never shows a raw value (UX "Labels"): the PRG progress
    // steps' `state` leaves are the first that reach here.
    block.kind = 'scalar'
    block.yours = copyText('requestSync.field.otherValue')
  } else if (kind === 'text') {
    block.yours = plainText(o.value)
  } else if (kind === 'list') {
    block.added = listDifference(o.value, o.serverValue)
    block.removed = listDifference(o.serverValue, o.value)
  } else {
    block.yours = describeScalar(o.path, o.value, f)
    if (o.serverValue !== undefined) {
      block.savedNow = describeScalar(o.path, o.serverValue, f)
    }
  }

  if (o.status === 'stale') {
    block.changedByLine = copyText('requestSync.notice.changedBy', {
      by: o.by,
      org: o.org,
      datetime: o.at ? f.datetime(o.at) : null
    })
  }

  return block
}

/** [{ rowLabel, fields: [...] }] grouped by row, in the order they failed. */
export function groupsFor (root, outcomes) {
  var groups = []
  var index = {}

  outcomes.forEach(function (o) {
    var key = o.kind + ':' + o.id
    if (!index[key]) {
      index[key] = { rowLabel: rowLabelOf(root, o), fields: [] }
      groups.push(index[key])
    }
    index[key].fields.push(fieldBlock(root, o))
  })

  return groups
}

// The adopted step row of a failed outcome (a step save's own row; for a decision
// field, the step row its approval command addressed), or null.
function stepRowOf (o) {
  var id = o.decisionStepId !== undefined ? o.decisionStepId : (o.kind === 'step' ? o.id : null)
  var rec = id !== null && id !== undefined ? sync.findRecord('step', id) : null

  return rec ? rec.row : null
}

// The locked row of a refused outcome: the step row a step save addressed, or, for the
// request's own fields (the action row: step 3's Agreement Type, `state.step3.docType`),
// the party-1 row of the step the path belongs to, as the refusal's `action.steps[]`
// adopt put it on the action (2026-10-15).
function lockedRowOf (o) {
  return stepRowOf(o) || (o.kind === 'action' ? partyOneRow(o, sync.stepNoOfOutcome(o)) : null)
}

// A content save refused because the approval is locked: who locked it and when,
// from the row the refusal's body put on screen (a lock the one-time migration took
// has no person, and nothing is dated).
function approvalLockedLead (root, o) {
  var step = lockedRowOf(o) || {}
  var by = step.approvalLocked_by || null
  var f = fmt(root)
  var me = root.userName ? root.userName() : null

  return copyText('requestSync.lock.approvalLocked', {
    by: by,
    self: !!by && by === me,
    datetime: by && step.approvalLocked_at ? f.datetime(step.approvalLocked_at) : null
  })
}

// Can THIS viewer unlock it? (The footer says what to do next.) The app root
// carries the access helpers; a stand-in without them cannot.
function canUnlockHere (root, o) {
  var step = lockedRowOf(o)
  var action = sync.findRecord('action', (o.meta || {}).actionId)

  if (!step || !root || typeof root.isTowerCo !== 'function' || typeof root.canAccess !== 'function') {
    return false
  }

  return canUnlock(root, action ? action.row : null, step)
}

// The party-1 row of step `n`, as the adoption put it on the action (the one that says
// who completed the step and when), or null.
function partyOneRow (o, n) {
  var action = sync.findRecord('action', (o.meta || {}).actionId)
  var steps = action && action.row && action.row.steps ? action.row.steps : []

  for (var i = 0; i < steps.length; i++) {
    if (Number(steps[i].stepNo) === Number(n) && Number(steps[i].partyNo) === 1) {
      return steps[i]
    }
  }

  return null
}

// Can THIS viewer re-open a step (the Complete Step box's own gate)? The app root
// carries the access helpers; a stand-in without them cannot.
// The same gate as the hint's `canReopenStep()`: a Tower Co manager who can write
// (`canWriteStep()` is "Tower Co and not view-only"; the app root has no `canWriteStep`, so a
// stand-in that has one is asked, else it is spelled out).
function canReopenHere (root) {
  var canWrite = root && typeof root.canWriteStep === 'function'
    ? root.canWriteStep()
    : !(root && typeof root.isViewOnly === 'function' && root.isViewOnly())

  return !!root && typeof root.isTowerCo === 'function' && root.isTowerCo() &&
    typeof root.isManager === 'function' && root.isManager() && canWrite
}

function isCounterpartyViewer (root) {
  return !!root && typeof root.isTowerCo === 'function' && !root.isTowerCo()
}

// XOL-596 (completed steps): "{User name} completed Step n on {datetime}, so your change
// wasn't saved." Tower Co viewers get the names; a counterparty gets the Tower Co's.
// `what` is a key of requestSync.completed.what.*.
export function completedLead (root, o, what) {
  var n = sync.stepNoOfOutcome(o)

  return completedLeadFor(root, { n: n, p1: n ? partyOneRow(o, n) : null, what: what })
}

// The same, from the step number and its party-1 row (`opts.p1`, or null) directly: the
// record-level refusals (files, equipment, backhaul, sharer, lock) have no field outcome.
export function completedLeadFor (root, opts) {
  var n = opts.n
  var p1 = opts.p1
  var f = fmt(root)
  var me = root.userName ? root.userName() : null
  var by = p1 && p1.completed_by ? p1.completed_by : null
  var whatText = copyText('requestSync.completed.what.' + opts.what)

  if (isCounterpartyViewer(root)) {
    // A content field keeps the existing sentence; anything else names the step.
    if (opts.what === 'field') {
      return copyText('requestSync.lock.stepComplete', { towerCo: towerCo(root) })
    }

    return copyText('requestSync.completed.leadCounterparty', { towerCo: towerCo(root), n: n, what: whatText })
  }

  return copyText('requestSync.completed.lead', {
    n: n,
    by: by,
    self: !!by && by === me,
    datetime: by && p1.completed_at ? f.datetime(p1.completed_at) : null,
    what: whatText
  })
}

// What a refused outcome was about: the equipment list, a decision, or a field.
function completedWhat (o) {
  if (isDecisionOutcome(o)) {
    return 'decision'
  }

  return o.path === 'state.step2.equipment' ? 'equipmentEdit' : 'field'
}

export function completedFooter (root, n, kind) {
  return copyText('requestSync.completed.footer', {
    n: n,
    canReopen: canReopenHere(root),
    counterparty: isCounterpartyViewer(root),
    towerCo: towerCo(root),
    kind: kind || 'change'
  })
}

// UX item 3: which lock was it? Judged against the state the recovery adopted.
function lockLead (root, o) {
  var m = o.meta || {}
  var action = sync.findRecord('action', m.actionId)
  var row = action ? action.row : null
  var steps = row && row.steps ? row.steps : []
  var p1 = null
  var i

  if (o.code === 'approval_locked') {
    return approvalLockedLead(root, o)
  }

  // A completed step (403 step_completed) is checked first: it is the actual blocker,
  // whichever other lock the field is also under.
  if (o.code === 'step_completed') {
    return completedLead(root, o, completedWhat(o))
  }

  for (i = 0; i < steps.length; i++) {
    if (Number(steps[i].stepNo) === 2 && Number(steps[i].partyNo) === 1) {
      p1 = steps[i]
    }
  }

  if (o.code === 'equipment_row_locked') {
    return copyText('requestSync.lock.equipmentRow')
  }

  if (o.path === 'state.step2.equipment') {
    if (p1 && !(p1.state && p1.state.allowClientEquipmentInput)) {
      return copyText('requestSync.lock.equipmentOff', { towerCo: towerCo(root) })
    }
    if (p1 && (p1.approved || p1.completed)) {
      return copyText('requestSync.lock.equipmentStep', { approved: !!p1.approved })
    }
  }

  if (o.path === 'state.tpType' || o.path === 'state.siteId') {
    if (p1 && p1.approvedStatus === 'Approved') {
      return copyText('requestSync.lock.tpApproved')
    }
  }

  for (i = 0; i < steps.length; i++) {
    if (Number(steps[i].stepNo) === Number(m.stepNo) && Number(steps[i].partyNo) === 1 && steps[i].completed) {
      return copyText('requestSync.lock.stepComplete', { towerCo: towerCo(root) })
    }
  }

  return copyText('requestSync.lock.fallback', { message: o.message })
}

// Who closed (or rejected) a request, and when, from the rows the adopt put on the
// action: { status: 'Closed' | 'Rejected', by, date } with `date` formatted, and `by` /
// `date` null when the rows do not say. A Rejected request was rejected at its party-1
// step 2; a Closed one by whoever approved / completed it.
export function closedWho (root, action) {
  var row = action || {}
  var rejected = row.status === 'Rejected'
  var f = fmt(root)
  var by = null
  var at = null

  if (rejected) {
    var steps = row.steps || []
    for (var i = 0; i < steps.length; i++) {
      if (Number(steps[i].stepNo) === 2 && Number(steps[i].partyNo) === 1) {
        by = steps[i].approved_by
        at = steps[i].approved_at
      }
    }
  } else {
    by = row.approved_by || row.completed_by
    at = row.approved_at || row.completed_at
  }

  return { status: rejected ? 'Rejected' : 'Closed', by: by || null, date: at ? f.date(at) : null }
}

// The `request_closed` notice (UX item 6): title and body, built from the rows
// the adopt put on the action. Used for a content save AND for a command.
export function closedLead (root, o) {
  var m = o.meta || {}
  var action = sync.findRecord('action', m.actionId)
  var who = closedWho(root, action ? action.row : {})

  return {
    title: copyText('requestSync.closed.title', { status: who.status }),
    body: copyText('requestSync.closed.body', { status: who.status, by: who.by, date: who.date })
  }
}

// Highest precedence first: the notice is titled and led by the worst cause.
var PRECEDENCE = ['closed', 'read-only', 'locked', 'stale', 'network', 'failed']

function categoryOf (o) {
  if (o.status === 'closed') { return 'closed' }
  if (o.status === 'read-only') { return 'read-only' }
  if (o.status === 'locked') { return 'locked' }
  if (o.status === 'stale') { return 'stale' }
  if (o.status === 'network') { return 'network' }
  return 'failed'
}

// Is this a decision field's outcome (XOL-596)? It carries the step its approval
// command addressed.
function isDecisionOutcome (o) {
  return o.decisionStepId !== undefined
}

// The lead of a stale section whose outcomes are ONE decision group: the facts of the
// decision now on screen (who, what), from the step row the refusal adopted.
function decisionStaleLead (root, stale) {
  var o = stale[0]
  var step = stepRowOf(o) || {}
  var action = sync.findRecord('action', (o.meta || {}).actionId)
  var me = root.userName ? root.userName() : null
  var by = o.by || step.approved_by || null
  var legal = o.path === 'state.step3.legalApproved'

  return copyText('requestSync.decision.stale.lead', {
    kind: legal ? 'legal' : 'status',
    n: step.stepNo !== undefined ? step.stepNo : (o.meta || {}).stepNo,
    status: step.approvedStatus,
    approved: !!step.approved,
    docType: action && action.row.state && action.row.state.step3 ? action.row.state.step3.docType : null,
    by: by,
    self: !!by && by === me
  })
}

function staleLead (root, stale) {
  // Every stale outcome is one decision group: the decision's own lead.
  if (stale.every(isDecisionOutcome) && stale.every(function (o) { return o.decisionStepId === stale[0].decisionStepId })) {
    return decisionStaleLead(root, stale)
  }

  var n = stale.length
  var by = stale[0].by
  var same = stale.every(function (o) { return o.by === by })
  var me = root.userName ? root.userName() : null

  if (same && by && by === me) {
    return copyText('requestSync.stale.lead.self', { n: n })
  }
  if (same && by) {
    return copyText('requestSync.stale.lead.named', { by: by, org: stale[0].org, n: n })
  }
  return copyText('requestSync.stale.lead.unknown', { n: n })
}

function staleFooter (stale) {
  var p = stale[0].path
  if (stale.every(isDecisionOutcome) && stale.every(function (o) { return o.decisionStepId === stale[0].decisionStepId })) {
    return copyText(p === 'state.step3.legalApproved' ? 'requestSync.decision.stale.footerLegal' : 'requestSync.decision.stale.footer')
  }
  if (p === 'state.step2.equipment') {
    return copyText('requestSync.stale.footer.equipment')
  }
  if (p === 'emails.tower' || p === 'emails.telco') {
    return copyText('requestSync.stale.footer.recipients')
  }
  return copyText('requestSync.stale.footer')
}

/**
 * The merged error notice of a burst: { type, title, lead, footer, groups,
 * retry, persistent }. null when nothing in the burst needs one (a lone 428 has
 * its own prompt).
 */
export function failureNotice (root, failed, savedCount, burst) {
  var shown = failed.filter(function (o) { return o.status !== 'version-required' && o.status !== 'blocked' })
  if (shown.length === 0) {
    return null
  }

  var by = {}
  shown.forEach(function (o) {
    var c = categoryOf(o)
    by[c] = by[c] || []
    by[c].push(o)
  })

  var cat = null
  for (var i = 0; i < PRECEDENCE.length && !cat; i++) {
    if (by[PRECEDENCE[i]]) {
      cat = PRECEDENCE[i]
    }
  }

  var first = by[cat]
  var n = shown.length
  var notice = { type: 'warning', groups: groupsFor(root, shown), persistent: true }

  if (cat === 'closed') {
    var cl = closedLead(root, first[0])
    notice.title = cl.title
    notice.lead = cl.body
  } else if (cat === 'read-only' || cat === 'failed') {
    notice.type = 'error'
    notice.title = copyText('requestSync.readOnly.title')
    notice.lead = copyText('requestSync.readOnly.body')
    var rid = first[0].requestId
    if (rid) {
      notice.footer = copyText('requestSync.readOnly.reference', { requestId: rid })
    }
  } else if (cat === 'locked') {
    notice.title = copyText('requestSync.lock.title')
    notice.lead = lockLead(root, first[0])
    if (first[0].code === 'approval_locked') {
      notice.footer = copyText('requestSync.lock.approvalLockedFooter', { canUnlock: canUnlockHere(root, first[0]) })
    } else if (first[0].code === 'step_completed') {
      notice.footer = completedFooter(root, sync.stepNoOfOutcome(first[0]), 'change')
    }
  } else if (cat === 'stale') {
    notice.title = copyText('requestSync.stale.title', { n: first.length })
    notice.lead = staleLead(root, first)
    notice.footer = staleFooter(first)
  } else {
    // network: the values are still on screen, so no list.
    notice.type = 'error'
    notice.title = copyText('requestSync.network.title')
    notice.lead = copyText('requestSync.network.lead', { labels: first.map(function (o) { return labelOf(root, o) }) })
    notice.groups = []
    notice.retry = true
    notice.kind = 'network'
    notice.outcomes = first
  }

  var extra = []
  if (burst && burst.reason === 'leave') {
    extra.push(copyText('requestSync.leave.blocked'))
  }
  // The ordering rule: the remarks save that must come first failed, so the
  // reject / complete / e-mail that was waiting on it was not sent.
  //   Complete Step ticked during a pause (`completeStep`) names a failed DECISION or
  //   a failed save of THIS step's own fields (XOL-596 completed steps: that save could
  //   never be retried once the step is read-only); a failure of an unrelated action
  //   field (emails.*, a Site -> Actions remark) does not stop the completion.
  if (burst && burst.ordering && (burst.ordering.kind !== 'completeStep' || shown.some(function (o) {
    return isDecisionOutcome(o) || sync.stepNoOfOutcome(o) === Number(burst.ordering.n)
  }))) {
    extra.push(copyText('requestSync.ordering.notSent.' + burst.ordering.kind, { rtx: burst.ordering.rtx, n: burst.ordering.n }))
  }
  if (extra.length) {
    notice.footer = (notice.footer ? notice.footer + ' ' : '') + extra.join(' ')
  }
  notice.savedLine = savedCount > 0 ? copyText('requestSync.notice.savedLine') : null
  notice.count = n

  return notice
}

/** "Saved: A, B." (1-3 fields) or "Saved n changes."; with a request prefix when needed. */
export function successMessage (root, saved, burst) {
  var labels = []
  var requests = {}

  saved.forEach(function (o) {
    var l = labelOf(root, o)
    if (labels.indexOf(l) === -1) {
      labels.push(l)
    }
    requests[(o.meta || {}).actionId] = o.meta
  })

  var ids = Object.keys(requests)
  // Name the request when more than one is in the burst, when its drawer is gone, and
  // when the drawer is CLOSING: a close waits for its saves, so the drawer is still open
  // when "Saved" is built (reason 'leave'), and a retry of a blocked close (keep-trying)
  // is about to close it too. Either way the card is read over the list, not the drawer.
  var closing = !!burst && (burst.reason === 'leave' || burst.reason === sync.REASON_KEEP_TRYING)
  var prefix = ids.length > 1 || (burst && burst.scope && burst.scope.destroyed) || closing
  var m = requests[ids[0]] || {}

  return copyText('requestSync.saved.fields', {
    labels: labels,
    rtx: prefix ? m.rtx : null,
    id: m.actionId
  })
}

/**
 * Raise the popup of one settled burst: ONE section (or the Saved text) of the
 * save-status card. `opts.retry` (optional) replaces the Try again handler of the
 * network section.
 */
export function reportBurst (root, burst, summary, opts) {
  opts = opts || {}

  // A later successful save of fields the connection-problem section lists takes
  // them off it; when none are left the section resolves (UX: UF12).
  var net = savePopup.networkSection()
  var savedKeys = summary.saved.map(savePopup.keyOf)
  if (net && savedKeys.length > 0) {
    var before = net.outcomes.length
    var gone = savePopup.resolveNetwork(savedKeys)
    var rest = savePopup.networkSection()
    if (!gone && rest && rest.outcomes.length !== before) {
      // Some of the listed fields saved: the section now names the others.
      savePopup.update(rest.id, { lead: failureNotice(root, rest.outcomes, 0, null).lead })
    }
  }

  // A retry of a blocked close / navigation during a connection problem
  // (REASON_KEEP_TRYING) that failed on the network again raises nothing: the
  // first "connection problem" section is still up. Any other failure speaks.
  var quiet = !!burst && burst.reason === sync.REASON_KEEP_TRYING && summary.failed.length > 0 &&
    summary.failed.every(function (o) { return o.status === 'network' })

  var failed = summary.failed
  var section = savePopup.networkSection()
  if (section && failed.length > 0 && failed.every(function (o) { return o.status === 'network' })) {
    // The same connection problem again: its one section lists every field.
    failed = mergeOutcomes(section.outcomes, failed)
  }

  var notice = (summary.failed.length > 0 && !quiet) ? failureNotice(root, failed, summary.saved.length, burst) : null

  if (notice) {
    var stashIds = summary.failed.map(function (o) { return o.stashId }).filter(function (v) { return v !== undefined })

    savePopup.notify({
      type: notice.type,
      title: notice.title,
      lead: notice.lead,
      groups: notice.groups,
      footer: notice.footer,
      savedLine: notice.savedLine,
      kind: notice.kind || null,
      outcomes: notice.outcomes || [],
      keys: (notice.outcomes || []).map(savePopup.keyOf),
      retry: !!notice.retry,
      onRetry: notice.retry ? (opts.retry || function () { sync.flushAllAndWait({ reason: 'retry' }) }) : null,
      onDismiss: function () { sync.clearStash(stashIds) },
      duration: 0
    })
    return { kind: 'notice', notice: notice }
  }

  if (summary.saved.length > 0) {
    savePopup.success(successMessage(root, summary.saved, burst))
    return { kind: 'success' }
  }

  return { kind: 'none' }
}

function mergeOutcomes (existing, fresh) {
  var out = existing.slice()
  var keys = out.map(savePopup.keyOf)
  fresh.forEach(function (o) {
    var k = savePopup.keyOf(o)
    var at = keys.indexOf(k)
    if (at === -1) {
      keys.push(k)
      out.push(o)
    } else {
      out[at] = o
    }
  })
  return out
}

/**
 * The 428 prompt (UX item 9): "This page is out of date", with the unsaved
 * fields listed and a Copy for each. At most once per page load.
 */
export function promptReloadDialog (root, first) {
  if (!first) {
    return null
  }
  var h = root.$createElement
  var pending = sync.pendingFields({ drafts: true, dirtyOnly: true }).map(function (p) {
    return { kind: p.meta.kind, id: p.meta.id, path: p.path, value: p.yours, meta: p.meta }
  })
  var groups = pending.length ? groupsFor(root, pending) : []

  // "These changes were not saved. Copy anything you need …:" ends in a colon, so it
  // INTRODUCES the list: it goes between the lead and the groups, not after them.
  var vnode = h(StaleSaveNotice, {
    props: {
      groups: groups,
      lead: copyText('requestSync.reload.lead'),
      intro: groups.length ? copyText('requestSync.reload.unsaved') : null
    }
  })

  return root.$confirm(vnode, copyText('requestSync.reload.title'), {
    confirmButtonText: copyText('requestSync.reload.confirm'),
    cancelButtonText: copyText('requestSync.reload.cancel'),
    type: 'warning',
    closeOnClickModal: false,
    closeOnPressEscape: true,
    customClass: 'xol-reload-box'
  }).then(function () {
    window.location.reload()
  }, function () {})
}

/**
 * Wire the burst reporter, the reload prompter and the ONE save-status card at app
 * level (main.js, once). The card is mounted on document.body, outlives every
 * drawer and is cleared by the session reset hook (lib/savePopup).
 */
export function installSyncNotices (root) {
  sync.setBurstReporter(function (burst, summary) {
    reportBurst(root, burst, summary)
  })
  sync.setReloadPrompter(function (self, ctx, info) {
    promptReloadDialog(root, info.first)
  })
  mountSavePopup(root)
}

function mountSavePopup (root) {
  unmountSavePopup()
  if (typeof document === 'undefined') {
    return
  }
  var host = document.createElement('div')
  document.body.appendChild(host)
  // `parent` gives the card the app's global mixins (isViewOnly); a plain object
  // root (a spec's stand-in) has none to give.
  popupVm = new Vue({
    parent: root && root._isVue ? root : undefined,
    render: function (h) { return h(SaveStatusPopup) }
  }).$mount(host)
}

function unmountSavePopup () {
  if (popupVm) {
    var el = popupVm.$el
    popupVm.$destroy()
    if (el && el.parentNode) {
      el.parentNode.removeChild(el)
    }
    popupVm = null
  }
}

export function _resetNoticesForTests () {
  unmountSavePopup()
  savePopup.reset()
}
